import { useReducer, useMemo } from "react";
import useReactRouter from "use-react-router";

import flowReducer, {
  actionTypes,
  initialState,
  getStepLength,
  getIsSubmitting,
  getStepById,
  getFormSteps,
  getHasSteps,
  getFirstUnsubmittedStep,
  getIsPristine,
  getStepsUntilAndIncludingFirstUnsubmitted,
  getNextStepById
} from "./reducer";

import {
  getRedirectUrl,
  calculateAllowedRoutes,
  getIsStepAllowedByStepId,
  mapSteps,
  formatReviewStepData,
  formatFlowFormData,
  formatUrl,
  isStepActiveById
} from "./utils";

import PropTypes from "prop-types";

const useFlowMachine = ({ model, onSubmit, onRouteChange }) => {
  const { history, location } = useReactRouter();
  const [state, dispatch] = useReducer(flowReducer, {
    ...initialState,
    steps: mapSteps(model.steps)
  });

  const allowedRoutes = useMemo(
    () =>
      calculateAllowedRoutes(
        getIsPristine(state),
        getFirstUnsubmittedStep(state),
        getIsSubmitting(state),
        getStepsUntilAndIncludingFirstUnsubmitted(state)
      ),
    [state]
  );

  const totalStepAmount = getStepLength(state);
  const redirectUrl = getRedirectUrl(
    getHasSteps(state),
    getFirstUnsubmittedStep(state)
  );
  const isSubmitting = getIsSubmitting(state);
  const formReviewData = formatReviewStepData(getFormSteps(state));

  const flowSteps = state.steps.map(step => {
    return {
      ...step,
      isAllowed: getIsStepAllowedByStepId(allowedRoutes, step.id),
      isActive: isStepActiveById(location.pathname, step.id)
    };
  });

  const handleFlowSubmit = async (e, stepId) => {
    e.preventDefault();
    const formData = formatFlowFormData(getFormSteps(state));
    // dispatch({ type: actionTypes.STEP_SUBMITTED, payload: { id: stepId, renderedFormData: formData } });
    dispatch({ type: actionTypes.FLOW_SUBMIT_START });

    /**
     * From here on, regardless of outcome ,the parent application will unmount this module. So this is the end of the line for us... :(
     */
    await onSubmit(formData);
  };

  const handleStepSubmit = ({ formData }, id) => {
    dispatch({
      type: actionTypes.STEP_SUBMITTED,
      payload: { id, renderedFormData: formData }
    });
    history.push(formatUrl(getNextStepById(state, id).id), {
      scrollIntoView: true
    });
  };

  const handleStepChanged = ({ formData }, id) => {
    dispatch({ type: actionTypes.STEP_CHANGED, payload: { id, formData } });
  };

  const handleRouteChanged = stepId => {
    onRouteChange(getStepById(state, stepId));
  };

  return {
    steps: flowSteps,
    allowedRoutes: allowedRoutes,
    totalStepAmount: totalStepAmount,
    redirectUrl: redirectUrl,
    isSubmitting: isSubmitting,
    formReviewData: formReviewData,
    onStepSubmit: handleStepSubmit,
    onStepChange: handleStepChanged,
    onFlowSubmit: handleFlowSubmit,
    onRouteChange: handleRouteChanged
  };
};

useFlowMachine.propTypes = {
  model: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onRouteChange: PropTypes.func.isRequired
};

export default useFlowMachine;
